import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Star, X } from "lucide-react";
import type { MidiaDaBibliotecaDto } from "@base/contracts";
import { Button } from "@/components/ui/button";
import { MidiaAutenticada } from "../../referencias/presentation/midia-autenticada";
import {
  ehVideoDaBiblioteca,
  formatarDuracao,
  formatarTamanho,
  formatarQuando,
  rotuloDaOrigem,
  rotuloDoTipo,
} from "../domain/rotulos-da-biblioteca";

type Props = {
  readonly item: MidiaDaBibliotecaDto;
  readonly temAnterior: boolean;
  readonly temProximo: boolean;
  readonly podeFavoritar: boolean;
  readonly onFechar: () => void;
  readonly onAnterior: () => void;
  readonly onProximo: () => void;
  readonly onFavoritar: (favorita: boolean) => void;
};

export function LightboxBiblioteca({
  item,
  temAnterior,
  temProximo,
  podeFavoritar,
  onFechar,
  onAnterior,
  onProximo,
  onFavoritar,
}: Props) {
  const fecharRef = useRef<HTMLButtonElement>(null);
  const video = ehVideoDaBiblioteca(item);
  const detalhe = [
    rotuloDoTipo(item),
    item.larguraPx && item.alturaPx
      ? `${item.larguraPx} × ${item.alturaPx}`
      : null,
    formatarDuracao(item.duracaoSegundos),
    formatarTamanho(item.tamanhoBytes),
    rotuloDaOrigem(item),
    formatarQuando(item.criadoEm),
  ]
    .filter(Boolean)
    .join(" · ");

  useEffect(() => {
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    fecharRef.current?.focus();
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [item.id]);

  useEffect(() => {
    function aoTecla(evento: KeyboardEvent) {
      if (evento.key === "Escape") {
        evento.preventDefault();
        onFechar();
      }
      if (evento.key === "ArrowLeft" && temAnterior) {
        evento.preventDefault();
        onAnterior();
      }
      if (evento.key === "ArrowRight" && temProximo) {
        evento.preventDefault();
        onProximo();
      }
    }
    document.addEventListener("keydown", aoTecla);
    return () => document.removeEventListener("keydown", aoTecla);
  }, [onAnterior, onFechar, onProximo, temAnterior, temProximo]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.nome}
      className="fixed inset-0 flex items-center justify-center p-0 sm:p-4"
      style={{ zIndex: "var(--rk-z-modal)" }}
    >
      <button
        type="button"
        aria-label="Fechar visualização"
        className="absolute inset-0 cursor-pointer bg-overlay backdrop-blur-[6px]"
        onClick={onFechar}
      />
      <div className="relative z-[1] flex h-full w-full max-w-[1100px] flex-col overflow-hidden bg-background sm:h-[min(92dvh,860px)] sm:rounded-2xl sm:border sm:border-border sm:shadow-lg">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="truncate text-sm font-medium text-foreground">
              {item.nome}
            </p>
            <p className="truncate text-xs text-muted-foreground">{detalhe}</p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {podeFavoritar ? (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="cursor-pointer size-9"
                aria-pressed={item.favorita}
                aria-label={
                  item.favorita
                    ? "Tirar dos favoritos"
                    : "Marcar como favorito"
                }
                onClick={() => onFavoritar(!item.favorita)}
              >
                <Star
                  className="size-4"
                  aria-hidden
                  fill={item.favorita ? "currentColor" : "none"}
                />
              </Button>
            ) : null}
            <Button
              ref={fecharRef}
              type="button"
              variant="secondary"
              size="icon"
              className="cursor-pointer size-9"
              aria-label="Fechar"
              onClick={onFechar}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        </header>

        <div className="relative min-h-0 flex-1 bg-foreground">
          {temAnterior ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-1/2 left-3 z-[1] size-9 -translate-y-1/2 cursor-pointer"
              aria-label="Arquivo anterior"
              onClick={onAnterior}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
          ) : null}
          <div className="absolute inset-3 sm:inset-5">
            <MidiaAutenticada
              src={item.previewUrl}
              poster={video ? item.capaUrl : null}
              tipo={video ? "video" : "image"}
              controls={video}
              alt={item.nome}
              className="size-full object-contain"
            />
          </div>
          {temProximo ? (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute top-1/2 right-3 z-[1] size-9 -translate-y-1/2 cursor-pointer"
              aria-label="Próximo arquivo"
              onClick={onProximo}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
